// Payment popup management utilities

export interface PaymentPopupResult {
  success: boolean;
  status?: string; // 'success', 'cancelled', 'failed', etc.
  redirectType?: string; // 'success', 'cancelled', 'failed'
  purchaseId?: string;
  transactionId?: string;
  error?: string;
  courseId?: string;
  serviceType?: string; // 'selfcare', etc.
  amount?: number;
  paymentMethod?: string;
  orderId?: string;
  rawResponse?: any; // Full server response for debugging
}

export interface PaymentPopupOptions {
  width?: number;
  height?: number;
  centerOnScreen?: boolean;
}

/**
 * Opens a payment popup window with the provided HTML form
 * @param htmlContent - The payment form HTML from backend
 * @param options - Popup window options
 * @returns Promise that resolves with payment result
 */
export const openPaymentPopup = (
  htmlContent: string,
  options: PaymentPopupOptions = {}
): Promise<PaymentPopupResult> => {
  return new Promise((resolve) => {
    const {
      width = 600,
      height = 700,
      centerOnScreen = true
    } = options;

    // Calculate popup position
    let left = 0;
    let top = 0;
    if (centerOnScreen) {
      left = (window.screen.width - width) / 2;
      top = (window.screen.height - height) / 2;
    }

    // Create new tab (removing window features to open in tab instead of popup)
    const popup = window.open(
      '',
      '_blank'
    );

    if (!popup) {
      resolve({
        success: false,
        error: 'Popup blocked. Please allow popups and try again.'
      });
      return;
    }

    // Listen for postMessage from callback pages
    const handleMessage = (event: MessageEvent) => {
      console.log('[PaymentPopup] Received postMessage:', event.data, 'from:', event.origin);
      
      // Verify origin for security
      if (event.origin !== window.location.origin) {
        console.log('[PaymentPopup] Ignoring message from different origin');
        return;
      }

      // Check if it's a payment result message
      if (event.data && event.data.type === 'PAYMENT_RESULT') {
        console.log('[PaymentPopup] Valid payment result received:', event.data);
        
        // Clean up
        window.removeEventListener('message', handleMessage);
        clearTimeout(timeoutId);
        clearInterval(popupCheckInterval);

        // Resolve with the result
        resolve({
          success: event.data.success,
          status: event.data.status,
          redirectType: event.data.redirectType,
          purchaseId: event.data.purchaseId,
          transactionId: event.data.transactionId,
          courseId: event.data.courseId,
          serviceType: event.data.serviceType,
          amount: event.data.amount,
          paymentMethod: event.data.paymentMethod,
          orderId: event.data.orderId,
          error: event.data.error,
          rawResponse: event.data.rawResponse
        });
      }
    };

    // Add message listener
    window.addEventListener('message', handleMessage);

    // Monitor popup closure
    const popupCheckInterval = setInterval(() => {
      if (popup.closed) {
        console.log('[PaymentPopup] Popup was closed by user');
        window.removeEventListener('message', handleMessage);
        clearTimeout(timeoutId);
        clearInterval(popupCheckInterval);
        
        resolve({
          success: false,
          error: 'Payment window was closed'
        });
      }
    }, 1000);

    // Write HTML content to popup
    console.log('[PaymentPopup] Writing payment form to popup window');
    console.log('[PaymentPopup] Form HTML length:', htmlContent.length);
    
    // First check if the form will auto-submit
    const willAutoSubmit = htmlContent.includes('submit()') || 
                          htmlContent.includes('onload') ||
                          htmlContent.includes('document.forms');
    
    console.log('[PaymentPopup] Form will auto-submit:', willAutoSubmit);
    
    popup.document.write(htmlContent);
    popup.document.close();
    
    console.log('[PaymentPopup] Payment form written and popup document closed');
    
    // If form doesn't auto-submit, try to submit it
    if (!willAutoSubmit) {
      console.log('[PaymentPopup] Form may not auto-submit, checking for form element...');
      setTimeout(() => {
        try {
          if (popup && !popup.closed && popup.document) {
            const forms = popup.document.forms;
            if (forms && forms.length > 0) {
              console.log('[PaymentPopup] Found form, attempting to submit...');
              forms[0].submit();
            } else {
              console.log('[PaymentPopup] No forms found in popup document');
            }
          }
        } catch (e) {
          console.log('[PaymentPopup] Could not access popup document:', e);
        }
      }, 500); // Small delay to ensure document is ready
    }

    // Timeout after 10 minutes
    const timeoutId = setTimeout(() => {
      window.removeEventListener('message', handleMessage);
      clearInterval(popupCheckInterval);
      
      if (!popup.closed) {
        popup.close();
      }
      
      resolve({
        success: false,
        error: 'Payment timeout. Please try again.'
      });
    }, 10 * 60 * 1000); // 10 minutes
  });
};

/**
 * Extracts course ID from URL path
 * @param url - The URL to extract from
 * @returns Course ID or null
 */
const extractCourseIdFromUrl = (url: string): string | null => {
  // Handle both /payment/ and /payments/ patterns
  const match = url.match(/\/buy-course\/([^\/]+)\/payments?\//);
  return match ? match[1] : null;
};

/**
 * Checks if popups are blocked in the browser
 * @returns boolean indicating if popups are blocked
 */
export const isPopupBlocked = (): boolean => {
  try {
    const popup = window.open('', 'test', 'width=1,height=1');
    if (!popup) {
      return true;
    }
    popup.close();
    return false;
  } catch (e) {
    return true;
  }
};

/**
 * Creates a payment form HTML string for testing
 * @param encRequest - Encrypted request data
 * @param accessCode - Access code for CCAvenue
 * @returns HTML string
 */
export const createPaymentFormHTML = (encRequest: string, accessCode: string): string => {
  return `
    <!DOCTYPE html>
    <html>
    <head>
      <title>Payment Processing</title>
      <style>
        body { font-family: Arial, sans-serif; padding: 20px; text-align: center; }
        .loader { border: 4px solid #f3f3f3; border-top: 4px solid #3498db; border-radius: 50%; width: 50px; height: 50px; animation: spin 1s linear infinite; margin: 20px auto; }
        @keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }
      </style>
    </head>
    <body>
      <h2>Processing Payment...</h2>
      <div class="loader"></div>
      <p>Please wait while we redirect you to the payment gateway.</p>
      <form method="POST" action="https://test.ccavenue.com/transaction/transaction.do?command=initiateTransaction" id="ccavenue_form">
        <input type="hidden" name="encRequest" value="${encRequest}"/>
        <input type="hidden" name="access_code" value="${accessCode}"/>
      </form>
      <script>
        document.getElementById('ccavenue_form').submit();
      </script>
    </body>
    </html>
  `;
};

/**
 * Validates payment form HTML
 * @param htmlContent - HTML content to validate
 * @returns boolean indicating if valid
 */
export const validatePaymentFormHTML = (htmlContent: string): boolean => {
  if (!htmlContent || typeof htmlContent !== 'string') {
    console.log('[PaymentPopup] Validation failed: HTML is empty or not a string');
    return false;
  }

  // Check for required form elements
  const hasForm = htmlContent.includes('<form');
  const hasEncRequest = htmlContent.includes('encRequest');
  const hasAccessCode = htmlContent.includes('access_code');
  const hasAction = htmlContent.includes('action=');
  
  console.log('[PaymentPopup] Form validation:', {
    hasForm,
    hasEncRequest,
    hasAccessCode,
    hasAction
  });
  
  // Check if form has auto-submit
  const hasAutoSubmit = htmlContent.includes('submit()') || htmlContent.includes('onload');
  console.log('[PaymentPopup] Form has auto-submit:', hasAutoSubmit);

  return hasForm && hasEncRequest && hasAccessCode && hasAction;
};